import { z } from "zod";
import { addDays, addMonths, todayIso } from "./obligations.js";
import type { LibraryUnit, ObligationBasis, ObligationKind } from "./library.js";
import {
  LIBRARY_UNIT_LABELS,
  LIBRARY_UNITS,
  OBLIGATION_BASES,
  OBLIGATION_KINDS,
  OBLIGATION_KIND_LABELS,
} from "./library.js";
import type { ObligationStatus, RecordResult } from "./obligations.js";
import type { ReservePlanDto } from "./reserve.js";
import { NLSFB, nlsfbGroupCode, nlsfbLabel, nlsfbMainCode, nlsfbNode } from "./nlsfb.js";

/**
 * MJOP (docs/ASSETS-MJOP.md, "MJOP"): begroting per jaar over alle gebouwen, alles exclusief
 * btw en in centen. Jaarbedragen worden berekend, nooit opgeslagen (besluit 11).
 */
export const WORK_TYPES = ["replacement", "maintenance", "inspection", "repair"] as const;
export type WorkType = (typeof WORK_TYPES)[number];
export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  replacement: "Vervanging",
  maintenance: "Onderhoud",
  inspection: "Inspectie",
  repair: "Herstel",
};

export const BUDGET_STATUSES = ["planned", "done", "cancelled"] as const;
export type BudgetStatus = (typeof BUDGET_STATUSES)[number];
export const BUDGET_STATUS_LABELS: Record<BudgetStatus, string> = {
  planned: "Gepland",
  done: "Uitgevoerd",
  cancelled: "Vervallen",
};

export const OVERRIDE_ACTIONS = ["shift", "skip", "amount"] as const;
export type OverrideAction = (typeof OVERRIDE_ACTIONS)[number];
export const OVERRIDE_ACTION_LABELS: Record<OverrideAction, string> = {
  shift: "Verschuiven",
  skip: "Overslaan",
  amount: "Ander bedrag",
};

export const MJOP_HORIZONS = [5, 10, 15, 20] as const;
export const DEFAULT_MJOP_HORIZON = 10;
/** Terugval als de CBS-cijfers niet op te halen zijn. */
export const CBS_FALLBACK_PERCENT = 3;

export interface MjopSettings {
  /** Jaarlijkse prijsstijging in procenten (bijv. 3 of 2.5). */
  indexPercent: number;
  /** Prijspeil van bedragen zonder eigen prijspeil. */
  baseYear: number;
  horizonYears: number;
}

/** Begrotingspost zoals de berekening hem nodig heeft. */
export interface BudgetItemInput {
  id: string;
  buildingId: string;
  assetId: string | null;
  workType: WorkType;
  description: string;
  elementCode: string | null;
  quantity: number;
  unitPriceCents: number;
  priceLevelYear: number | null;
  /** null = eenmalig. */
  cycleYears: number | null;
  firstYear: number;
  lastYear: number | null;
  status: BudgetStatus;
  /** Jaar van afronding bij `done`. */
  doneYear: number | null;
}

export interface BudgetOverrideInput {
  budgetItemId: string;
  /** Oorspronkelijk jaar van het uitvoermoment. */
  year: number;
  action: OverrideAction;
  newYear: number | null;
  amountCents: number | null;
  rebaseCycle: boolean;
  /** Waarom deze keer afwijkt (optioneel). */
  reason?: string | null;
}

/** Verplichting bij een asset of het gebouw zelf met kosten per keer (keuring, inspectie, controle, onderhoud). */
export interface BudgetObligationInput {
  id: string;
  buildingId: string;
  /** null = bij het gebouw zelf. */
  assetId: string | null;
  name: string;
  kind: ObligationKind;
  elementCode: string | null;
  intervalMonths: number | null;
  /** Wekelijks werk (dan is `intervalMonths` leeg). */
  intervalWeeks?: number | null;
  costCents: number | null;
  priceLevelYear: number | null;
  nextDueOn: string | null;
  /** Laatste jaar van uitvoering; null = doorlopend. */
  endYear?: number | null;
  notApplicable: boolean;
  /**
   * Onder een contractpost (docs/CONTRACTEN.md, C3). `period`: in de aanneemsom, telt in die tijd niet los mee (de
   * contractpost is de jaarpost). `visit`: prijs per keer uit het contract, per jaar de prijs die dan geldt.
   * Buiten `from`–`until` (looptijd) gelden weer de eigen kosten.
   */
  contract?: ObligationContractInput | null;
}

/** Prijs van een contractpost vanaf een datum: bedrag per periode, per keer of de raming per jaar (nacalculatie). */
export interface BudgetContractPrice {
  validFrom: string;
  cents: number;
}

export type ObligationContractInput =
  | { mode: "period"; from: string; until: string | null }
  | { mode: "visit"; from: string; until: string | null; prices: BudgetContractPrice[]; indexPercent: number };

/** Contractpost met een vast bedrag per periode (of een raming per jaar bij nacalculatie): één jaarpost in het MJOP. */
export interface BudgetContractLineInput {
  id: string;
  contractId: string;
  /** "Contract · post". */
  label: string;
  workType: WorkType;
  /** Gebouwen waarover het bedrag gelijk verdeeld wordt. */
  buildingIds: string[];
  from: string;
  /** Leeg = doorlopend (onbepaalde tijd of stilzwijgend verlengd). */
  until: string | null;
  /** Periodes per jaar: 12 (maand), 4 (kwartaal), 1 (jaar of raming per jaar). */
  perYear: number;
  prices: BudgetContractPrice[];
  /** Na de laatste prijs: index van het contract of de organisatie (0 = geen indexering). */
  indexPercent: number;
}

export interface BudgetRow {
  source: "item" | "obligation" | "contract";
  sourceId: string;
  buildingId: string;
  assetId: string | null;
  workType: WorkType;
  /** Omschrijving; bij verplichtingen de naam. */
  label: string;
  /** Binnen Inspectie: Keuring, Inspectie of Controle. */
  kindLabel: string | null;
  nlsfbCode: string | null;
  year: number;
  amountCents: number;
  /** Cyclus van de post in jaren; leeg bij eenmalig en bij verplichtingen (componentenmethode). */
  cycleYears?: number | null;
}

/** Indexfactor: (1 + index%)^(jaar − prijspeil), alleen voor jaren na het prijspeil. */
export function indexFactor(indexPercent: number, priceLevelYear: number, year: number): number {
  const n = year - priceLevelYear;
  return n > 0 ? Math.pow(1 + indexPercent / 100, n) : 1;
}

/** Bedrag op prijspeil geïndexeerd naar het jaar; afronden op hele centen na de vermenigvuldiging. */
export const indexed = (cents: number, indexPercent: number, priceLevelYear: number, year: number) =>
  Math.round(cents * indexFactor(indexPercent, priceLevelYear, year));

/**
 * Contractprijs op een dag: de laatste prijs die dan al geldt (vóór de eerste prijs: de eerste). Ligt de dag in een
 * later jaar dan die prijs, dan rekent het door met de index: prijs × (1 + index%)^(jaar − jaar van die prijs).
 */
export function contractPriceOn(
  prices: readonly BudgetContractPrice[],
  indexPercent: number,
  day: string,
): number | null {
  if (!prices.length) return null;
  const sorted = [...prices].sort((a, b) => a.validFrom.localeCompare(b.validFrom));
  let p = sorted[0]!;
  for (const x of sorted) if (x.validFrom <= day) p = x;
  return p.validFrom <= day
    ? indexed(p.cents, indexPercent, Number(p.validFrom.slice(0, 4)), Number(day.slice(0, 4)))
    : p.cents;
}

const inWindow = (day: string, from: string, until: string | null) => day >= from && (until === null || day <= until);

/** Jaarbedrag van een contractpost: per maand de prijs die dan geldt, alleen de maanden binnen de looptijd. */
export function contractLineYearCents(line: BudgetContractLineInput, year: number): number {
  let total = 0;
  for (let m = 1; m <= 12; m++) {
    const day = `${year}-${String(m).padStart(2, "0")}-15`;
    if (!inWindow(day, line.from, line.until)) continue;
    const p = contractPriceOn(line.prices, line.indexPercent, day);
    if (p) total += (p * line.perYear) / 12;
  }
  return Math.round(total);
}

/** Uitvoerjaren van een post vóór overrides en status (begrensd op `untilYear`). */
function baseYears(item: BudgetItemInput, untilYear: number): number[] {
  const last = Math.min(item.lastYear ?? untilYear, untilYear);
  if (!item.cycleYears) return item.firstYear <= last ? [item.firstYear] : [];
  const out: number[] = [];
  for (let y = item.firstYear; y <= last; y += item.cycleYears) out.push(y);
  return out;
}

const workTypeOf = (kind: ObligationKind): WorkType =>
  kind === "onderhoud" || kind === "overig" ? "maintenance" : "inspection";

interface ItemOccurrence {
  /** Oorspronkelijk jaar (sleutel van een override). */
  orig: number;
  year: number;
  /** Ander bedrag voor dat jaar; null = hoeveelheid × prijs. */
  amount: number | null;
  skipped: boolean;
  /** Reden van de laatste afwijking op dit uitvoermoment. */
  reason: string | null;
}

/** Uitvoermomenten van een post met overrides (verschuiven met of zonder nieuwe cyclus, overslaan, ander bedrag). */
function itemOccurrences(
  item: BudgetItemInput,
  overrides: readonly BudgetOverrideInput[],
  toYear: number,
): ItemOccurrence[] {
  // Ruim voorbij de horizon genereren: een verschoven cyclus kan later jaren terughalen.
  const occ = baseYears(item, toYear + 100).map((y) => ({
    orig: y,
    year: y,
    amount: null as number | null,
    skipped: false,
    reason: null as string | null,
  }));
  for (const o of [...overrides].sort((a, b) => a.year - b.year)) {
    const at = occ.find((x) => x.orig === o.year);
    if (!at) continue;
    if (o.reason) at.reason = o.reason;
    if (o.action === "skip") at.skipped = true;
    else if (o.action === "amount" && o.amountCents != null) at.amount = o.amountCents;
    else if (o.action === "shift" && o.newYear != null) {
      const delta = o.newYear - at.year;
      if (o.rebaseCycle) {
        // Nieuwe cyclus vanaf het nieuwe jaar: alle latere uitvoermomenten schuiven mee.
        for (const x of occ) if (x.orig >= o.year) x.year += delta;
      } else {
        at.year = o.newYear;
      }
    }
  }
  return occ;
}

/**
 * Uitvoerdata van een verplichting tot en met `toYear` (en het stopjaar): vanaf de vervaldatum, of
 * vandaag als die voorbij of onbekend is; daarna elk interval (maanden of weken). Eenmalig telt één
 * keer; zonder interval én zonder datum valt er niets te plannen.
 */
export function obligationDates(
  o: Pick<BudgetObligationInput, "intervalMonths" | "intervalWeeks" | "nextDueOn" | "endYear" | "notApplicable">,
  toYear: number,
  today: string = todayIso(),
): string[] {
  const weeks = o.intervalWeeks ?? null;
  if (o.notApplicable || (!o.intervalMonths && !weeks && !o.nextDueOn)) return [];
  const out: string[] = [];
  let d = o.nextDueOn && o.nextDueOn >= today ? o.nextDueOn : today;
  for (let guard = 0; guard < 3000; guard++) {
    const y = Number(d.slice(0, 4));
    if (y > toYear || (o.endYear != null && y > o.endYear)) break;
    out.push(d);
    if (weeks) d = addDays(d, 7 * weeks);
    else if (o.intervalMonths) d = addMonths(d, o.intervalMonths);
    else break;
  }
  return out;
}

/**
 * Begroting per jaar (docs/ASSETS-MJOP.md, "Berekening"):
 * 1. posten: eerste jaar, dan elke cyclus tot het laatste jaar; daarna overrides (verschuiven met of
 *    zonder nieuwe cyclus, overslaan, ander bedrag);
 * 2. verplichtingen met kosten en interval: uitvoermomenten vanaf de vervaldatum (of vandaag),
 *    per jaar opgeteld;
 * 3. bedrag = hoeveelheid × prijs, geïndexeerd vanaf het prijspeil;
 * 4. `cancelled` telt niet; `done` niet voor jaren tot en met het jaar van afronding.
 */
export function expandBudget(
  items: readonly BudgetItemInput[],
  obligations: readonly BudgetObligationInput[],
  overrides: readonly BudgetOverrideInput[],
  settings: MjopSettings,
  fromYear: number,
  toYear: number,
  today: string = todayIso(),
  contractLines: readonly BudgetContractLineInput[] = [],
): BudgetRow[] {
  const rows: BudgetRow[] = [];
  const byItem = new Map<string, BudgetOverrideInput[]>();
  for (const o of overrides) byItem.set(o.budgetItemId, [...(byItem.get(o.budgetItemId) ?? []), o]);

  for (const item of items) {
    if (item.status === "cancelled") continue;
    const level = item.priceLevelYear ?? settings.baseYear;
    const base = Math.round(item.quantity * item.unitPriceCents);
    for (const x of itemOccurrences(item, byItem.get(item.id) ?? [], toYear)) {
      if (x.skipped || x.year < fromYear || x.year > toYear) continue;
      if (item.status === "done" && item.doneYear != null && x.year <= item.doneYear) continue;
      rows.push({
        source: "item",
        sourceId: item.id,
        buildingId: item.buildingId,
        assetId: item.assetId,
        workType: item.workType,
        label: item.description,
        kindLabel: null,
        nlsfbCode: item.elementCode,
        cycleYears: item.cycleYears,
        year: x.year,
        // Een ander bedrag geldt voor dat jaar zoals ingevuld (niet nog eens geïndexeerd).
        amountCents: x.amount ?? indexed(base, settings.indexPercent, level, x.year),
      });
    }
  }

  for (const o of obligations) {
    const c = o.contract ?? null;
    if (!o.costCents && c?.mode !== "visit") continue;
    const level = o.priceLevelYear ?? settings.baseYear;
    const perYear = new Map<number, number>();
    for (const d of obligationDates(o, toYear, today)) {
      const y = Number(d.slice(0, 4));
      if (y < fromYear) continue;
      let cents = o.costCents ? indexed(o.costCents, settings.indexPercent, level, y) : 0;
      if (c && inWindow(d, c.from, c.until))
        cents = c.mode === "period" ? 0 : (contractPriceOn(c.prices, c.indexPercent, d) ?? cents);
      perYear.set(y, (perYear.get(y) ?? 0) + cents);
    }
    for (const [year, amountCents] of perYear) {
      if (!amountCents) continue;
      rows.push({
        source: "obligation",
        sourceId: o.id,
        buildingId: o.buildingId,
        assetId: o.assetId,
        workType: workTypeOf(o.kind),
        label: o.name,
        kindLabel: OBLIGATION_KIND_LABELS[o.kind],
        nlsfbCode: o.elementCode,
        year,
        amountCents,
      });
    }
  }

  // Contractposten met een vast bedrag: één jaarpost, gelijk verdeeld over de gebouwen.
  for (const l of contractLines) {
    if (!l.buildingIds.length) continue;
    for (let year = fromYear; year <= toYear; year++) {
      const total = contractLineYearCents(l, year);
      if (!total) continue;
      const each = Math.floor(total / l.buildingIds.length);
      l.buildingIds.forEach((buildingId, i) =>
        rows.push({
          source: "contract",
          sourceId: `${l.id}:${buildingId}`,
          buildingId,
          assetId: null,
          workType: l.workType,
          label: l.label,
          kindLabel: "Contract",
          nlsfbCode: null,
          year,
          // De rest van de deling bij het eerste gebouw, zodat het totaal klopt.
          amountCents: each + (i === 0 ? total - each * l.buildingIds.length : 0),
        }),
      );
    }
  }
  return rows.sort((a, b) => a.year - b.year || a.workType.localeCompare(b.workType));
}

/** Totalen per jaar en soort werk (voor de grafiek en de kerncijfers). */
export function totalsByYear(rows: readonly BudgetRow[], fromYear: number, toYear: number) {
  const years: { year: number; total: number; byType: Record<WorkType, number> }[] = [];
  for (let y = fromYear; y <= toYear; y++)
    years.push({ year: y, total: 0, byType: { replacement: 0, maintenance: 0, inspection: 0, repair: 0 } });
  for (const r of rows) {
    const t = years[r.year - fromYear];
    if (!t) continue;
    t.total += r.amountCents;
    t.byType[r.workType] += r.amountCents;
  }
  return years;
}

/**
 * Reserveringshorizon (docs/ASSETS-MJOP.md, "Reservering"): tot en met de verste eerstvolgende
 * vervanging vanaf `fromYear`, zodat elke vervanging minstens één keer meetelt; nooit korter dan de
 * planhorizon. `rows` loopt ruim voorbij de planhorizon.
 */
export function reserveHorizon(rows: readonly BudgetRow[], fromYear: number, toYear: number): number {
  const first = new Map<string, number>();
  for (const r of rows) {
    if (r.workType !== "replacement" || r.year < fromYear) continue;
    const k = `${r.source}:${r.sourceId}`;
    if (!first.has(k) || r.year < first.get(k)!) first.set(k, r.year);
  }
  return Math.max(toYear, ...first.values());
}

/** NL-SfB-hoofdgroep van een element, zoals in het dashboard en de export. */
export function mainGroupLabel(code: string | null): string {
  if (!code) return "Niet ingedeeld";
  const m = NLSFB.find((x) => x.code === nlsfbMainCode(code));
  return m ? `${m.code}- ${m.label}` : "Niet ingedeeld";
}

/**
 * Indeling van het MJOP op 3 posities NL-SfB (subelement, "56.2"). Een detailcode ("56.21") telt
 * onder zijn subelement; een code op elementniveau ("56"), of een detail zonder subelement in de
 * tabel, blijft op het element. Zonder code: null (Niet ingedeeld).
 */
export function nlsfbCategory(code: string | null | undefined): string | null {
  const n = nlsfbNode(code);
  if (!n) return null;
  if (n.level === 2) return n.code;
  const sub = n.code.slice(0, 4);
  return nlsfbNode(sub) ? sub : nlsfbGroupCode(n.code);
}

/** "56.2 Verwarming en koeling › opwekking centraal", "56 Verwarming en koeling" of "Niet ingedeeld". */
export function nlsfbCategoryLabel(category: string | null): string {
  return nlsfbLabel(category);
}

/**
 * Filter op NL-SfB: leeg = alles, "none" = niet ingedeeld, één cijfer = hoofdgroep ("5"),
 * anders een categorie op 3 posities of een element ("56.2", "56").
 */
export function matchesNlsfbFilter(code: string | null, filter: string | null | undefined): boolean {
  if (!filter) return true;
  const cat = nlsfbCategory(code);
  if (filter === "none") return cat === null;
  if (!cat) return false;
  if (filter.length === 1) return nlsfbMainCode(cat) === filter;
  return cat === filter;
}

export function nlsfbFilterLabel(filter: string | null | undefined): string | null {
  if (!filter) return null;
  if (filter === "none") return "Niet ingedeeld";
  return filter.length === 1 ? mainGroupLabel(filter) : nlsfbCategoryLabel(filter);
}

/** Hoofdgroep met subtotaal en daaronder de categorieën op 3 posities. */
export interface NlsfbTableGroup {
  key: string;
  amounts: number[];
  total: number;
  children: { key: string; amounts: number[]; total: number }[];
}

export function budgetTableByNlsfb(rows: readonly BudgetRow[], fromYear: number, toYear: number): NlsfbTableGroup[] {
  const mainOf = (r: BudgetRow) => {
    if (r.source === "contract") return "C";
    const c = nlsfbCategory(r.nlsfbCode);
    return c ? nlsfbMainCode(c) : "~";
  };
  // Volgorde zoals de tabel: 1–9, dan 0, Contracten, Niet ingedeeld als laatste.
  const order = (m: string) => (m === "~" ? 12 : m === "C" ? 11 : m === "0" ? 10 : Number(m));
  const mains = [...new Set(rows.filter((r) => r.year >= fromYear && r.year <= toYear).map(mainOf))].sort(
    (a, b) => order(a) - order(b),
  );
  return mains.map((m) => {
    const sub = rows.filter((r) => mainOf(r) === m);
    const [head] = budgetTable(sub, fromYear, toYear, () => "");
    const children =
      m === "~"
        ? []
        : m === "C"
          ? budgetTable(sub, fromYear, toYear, (r) => r.label)
          : budgetTable(sub, fromYear, toYear, (r) => nlsfbCategory(r.nlsfbCode)!).map((c) => ({
              ...c,
              key: nlsfbCategoryLabel(c.key),
            }));
    return {
      key: m === "~" ? "Niet ingedeeld" : m === "C" ? "Contracten" : mainGroupLabel(m),
      amounts: head!.amounts,
      total: head!.total,
      children,
    };
  });
}

/** Meerjarentabel: één rij per sleutel (hoofdgroep, gebouw, soort werk), bedragen per jaar. */
export function budgetTable(
  rows: readonly BudgetRow[],
  fromYear: number,
  toYear: number,
  keyOf: (r: BudgetRow) => string,
): { key: string; amounts: number[]; total: number }[] {
  const n = toYear - fromYear + 1;
  const m = new Map<string, number[]>();
  for (const r of rows) {
    if (r.year < fromYear || r.year > toYear) continue;
    const k = keyOf(r);
    const arr = m.get(k) ?? Array.from({ length: n }, () => 0);
    arr[r.year - fromYear]! += r.amountCents;
    m.set(k, arr);
  }
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "nl", { numeric: true }))
    .map(([key, amounts]) => ({ key, amounts, total: amounts.reduce((s, v) => s + v, 0) }));
}

/** Eén regel per bron (post of verplichting) met de bedragen per jaar: de postenlijst van de export. */
export interface BudgetLine {
  source: BudgetRow["source"];
  sourceId: string;
  buildingId: string;
  assetId: string | null;
  workType: WorkType;
  label: string;
  kindLabel: string | null;
  nlsfbCode: string | null;
  amounts: number[];
  total: number;
}

export function budgetLines(rows: readonly BudgetRow[], fromYear: number, toYear: number): BudgetLine[] {
  const n = toYear - fromYear + 1;
  const m = new Map<string, BudgetLine>();
  for (const r of rows) {
    if (r.year < fromYear || r.year > toYear) continue;
    const k = `${r.source}:${r.sourceId}`;
    let l = m.get(k);
    if (!l) {
      l = {
        source: r.source,
        sourceId: r.sourceId,
        buildingId: r.buildingId,
        assetId: r.assetId,
        workType: r.workType,
        label: r.label,
        kindLabel: r.kindLabel,
        nlsfbCode: r.nlsfbCode,
        amounts: Array.from({ length: n }, () => 0),
        total: 0,
      };
      m.set(k, l);
    }
    l.amounts[r.year - fromYear]! += r.amountCents;
    l.total += r.amountCents;
  }
  return [...m.values()].sort(
    (a, b) =>
      WORK_TYPES.indexOf(a.workType) - WORK_TYPES.indexOf(b.workType) ||
      Number(!a.nlsfbCode) - Number(!b.nlsfbCode) ||
      (a.nlsfbCode ?? "").localeCompare(b.nlsfbCode ?? "", "nl", { numeric: true }) ||
      a.label.localeCompare(b.label, "nl"),
  );
}

/** Eerste vervangingsjaar: bouwjaar + levensduur, of het lopende jaar als dat al voorbij is. */
export function replacementFirstYear(installedYear: number | null, lifespanYears: number, thisYear: number): number {
  return installedYear ? Math.max(installedYear + lifespanYears, thisYear) : thisYear + lifespanYears;
}

// ── API ──

const year = z.number().int().min(1900).max(2200);
const cents = z.number().int().min(0).max(10_000_000_000);
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null);

export const budgetItemCreateSchema = z.object({
  id: z.uuid(),
  workType: z.enum(WORK_TYPES),
  description: z.string().trim().min(1, "Omschrijving is verplicht").max(300),
  elementCode: text(10),
  quantity: z.number().positive().max(1_000_000).default(1),
  unit: z.enum(LIBRARY_UNITS).default("post"),
  unitPriceCents: cents,
  priceLevelYear: year.nullish(),
  cycleYears: z.number().int().min(1).max(100).nullish(),
  firstYear: year,
  lastYear: year.nullish(),
  notes: text(2000),
  /** Eis (wettelijk, verzekeraar …); bij losse regels in de uitvoeringentabel. */
  basis: z.enum(OBLIGATION_BASES).nullish(),
  /** Soort binnen inspectie/onderhoud (keuring, controle, overig …); null = volgt uit `workType`. */
  kind: z.enum(OBLIGATION_KINDS).nullish(),
});
export type BudgetItemCreate = z.input<typeof budgetItemCreateSchema>;

// Zonder standaardwaarden: anders zette elke wijziging (bijv. alleen het startjaar) hoeveelheid en eenheid terug op 1 post.
export const budgetItemUpdateSchema = budgetItemCreateSchema
  .omit({ id: true })
  .extend({ quantity: z.number().positive().max(1_000_000), unit: z.enum(LIBRARY_UNITS) })
  .partial()
  .extend({ status: z.enum(BUDGET_STATUSES).optional() });
export type BudgetItemUpdate = z.input<typeof budgetItemUpdateSchema>;

export const budgetOverrideSchema = z
  .object({
    action: z.enum(OVERRIDE_ACTIONS),
    newYear: year.nullish(),
    amountCents: cents.nullish(),
    rebaseCycle: z.boolean().default(true),
    reason: text(500),
  })
  .refine((o) => o.action !== "shift" || o.newYear != null, { message: "Kies het nieuwe jaar", path: ["newYear"] })
  .refine((o) => o.action !== "amount" || o.amountCents != null, {
    message: "Vul een bedrag in",
    path: ["amountCents"],
  });
export type BudgetOverrideBody = z.input<typeof budgetOverrideSchema>;

/** Vervanging bij een asset: levensduur, prijs per eenheid en hoeveelheid (uit de kennisbank, aanpasbaar). */
/**
 * Hoeveelheid van de vervanging tegenover die van de asset: de vervanging neemt aantal en eenheid van de asset
 * over, behalve bij eenheid *post* (forfaitair) of als de asset geen hoeveelheid (of 0) heeft. Wijkt het af, dan is
 * dat een eigen aantal in het MJOP (docs/ASSETS-MJOP.md, Hoeveelheid).
 */
export function replacementQuantityDiffers(
  asset: { quantity: number | null; unit: string | null },
  quantity: number,
  unit: string,
): boolean {
  if (unit === "post" || asset.quantity == null || asset.quantity <= 0) return false;
  return quantity !== asset.quantity || (asset.unit != null && asset.unit !== unit);
}

/** "120 m²" voor een hoeveelheid met eenheid. */
export const quantityText = (quantity: number, unit: string | null | undefined) =>
  `${quantity.toLocaleString("nl-NL")}${unit ? ` ${LIBRARY_UNIT_LABELS[unit as LibraryUnit] ?? unit}` : ""}`;

export const assetReplacementSchema = z.object({
  installedYear: year.nullish(),
  lifespanYears: z.number().int().min(1).max(200).nullable(),
  unitPriceCents: cents.nullable(),
  quantity: z.number().positive().max(1_000_000).default(1),
  unit: z.enum(LIBRARY_UNITS).default("st"),
  priceLevelYear: year.nullish(),
  /** Versie van de kennisbankregel waaruit overgenomen is. */
  libraryVersionUsed: z.number().int().min(1).nullish(),
});
export type AssetReplacementBody = z.input<typeof assetReplacementSchema>;

/** Raming herstelkosten bij een bevinding (excl. btw); leeg = geen herstelpost. */
export const issueEstimateSchema = z.object({
  estimatedCostCents: cents.nullable(),
  plannedRepairYear: year.nullish(),
});
export type IssueEstimateBody = z.input<typeof issueEstimateSchema>;

export const mjopSettingsSchema = z.object({
  indexPercent: z.number().min(-20).max(50),
  baseYear: year,
  horizonYears: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]),
});

export interface BudgetOverrideDto extends BudgetOverrideInput {
  reason: string | null;
}

export interface BudgetItemDto extends BudgetItemInput {
  unit: string;
  basis: ObligationBasis | null;
  kind: ObligationKind | null;
  notes: string | null;
  findingId: string | null;
  findingCode: string | null;
  assetCode: string | null;
  assetName: string | null;
  overrides: BudgetOverrideDto[];
}

export interface CbsSuggestion {
  /** Jaarmutatie van het laatste kwartaal, in procenten. */
  lastYearPercent: number | null;
  /** Gemiddelde jaarmutatie over 5 jaar. */
  fiveYearPercent: number | null;
  /** Bijv. "2026 2e kwartaal". */
  period: string | null;
  fallback: boolean;
  fetchedAt: string | null;
}

export interface MjopSettingsResponse extends MjopSettings {
  /** Basisjaar zoals ingesteld; null = het lopende jaar. */
  baseYearSetting: number | null;
  suggestion: CbsSuggestion;
}

export interface MjopResponse {
  settings: MjopSettings;
  fromYear: number;
  toYear: number;
  rows: BudgetRow[];
  /** Kosten na de planhorizon tot en met `reserveToYear`, alleen voor de reservering. */
  reserveRows: BudgetRow[];
  reserveToYear: number;
  /** Reservering per gebouw in beeld; zonder opgeslagen plan de standaard (`saved: false`). */
  reservePlans: ReservePlanDto[];
  buildings: { id: string; code: string; name: string }[];
}

export interface AssetReplacementDto {
  installedYear: number | null;
  lifespanYears: number | null;
  unitPriceCents: number | null;
  quantity: number;
  unit: string;
  priceLevelYear: number | null;
  libraryVersionUsed: number | null;
  /** Hoeveelheid van de asset zelf; de vervanging neemt die over tenzij er een eigen aantal is (of eenheid post). */
  assetQuantity: number | null;
  assetUnit: string | null;
  /** Eigen aantal in het MJOP dat afwijkt van de asset. */
  quantityOwn: boolean;
  /** De vervangingspost (als die er is), met het bedrag in het eerste jaar inclusief index. */
  item: {
    id: string;
    firstYear: number;
    cycleYears: number | null;
    status: BudgetStatus;
    indexedCents: number | null;
  } | null;
  /** Laatste jaar van de planhorizon; een vervanging daarna staat niet in de grafiek, wel in de reservering. */
  horizonEndYear: number;
  /**
   * Hoofdasset als dit een onderdeel is, met haar vervanging (jaar of null). Een onderdeel valt standaard
   * onder de vervanging van de hoofdasset; een eigen post telt apart mee (docs/ASSETS-MJOP.md, Onderdelen).
   */
  parent: { id: string; code: string; name: string; replacementYear: number | null } | null;
  /** Voorstel uit de kennisbank voor de code van de asset; leeg bij een onderdeel met de code van de hoofdasset. */
  suggestion: {
    lifespanYears: number | null;
    unitPriceCents: number | null;
    unit: string | null;
    priceLevelYear: number | null;
    from: string | null;
    indicative: boolean;
    version: number | null;
  } | null;
}

// ---------- Uitvoeringen (MJOP-tabel per gebouw) ----------

/**
 * Soort uitvoering in de MJOP-tabel: de keuringssoorten bij een asset, plus vervanging en herstel
 * (MJOP-posten). Een losse regel (zonder asset) is een post op gebouwniveau.
 */
export const PLAN_TYPES = ["vervanging", ...OBLIGATION_KINDS, "herstel"] as const;
export type PlanType = (typeof PLAN_TYPES)[number];
export const PLAN_TYPE_LABELS: Record<PlanType, string> = {
  vervanging: "Vervanging",
  ...OBLIGATION_KIND_LABELS,
  herstel: "Herstel",
};
/** Soorten die je zelf toevoegt: bij een asset (uitvoering) en als losse regel. */
export const ASSET_PLAN_TYPES = OBLIGATION_KINDS;
export const LOOSE_PLAN_TYPES = ["onderhoud", "keuring", "inspectie", "controle", "overig", "vervanging"] as const;
export type LoosePlanType = (typeof LOOSE_PLAN_TYPES)[number];

/** Losse regel: soort → `workType` + `kind` van de post. */
export function looseWorkType(t: LoosePlanType): { workType: WorkType; kind: ObligationKind | null } {
  if (t === "vervanging") return { workType: "replacement", kind: null };
  if (t === "onderhoud" || t === "overig") return { workType: "maintenance", kind: t === "overig" ? "overig" : null };
  return { workType: "inspection", kind: t === "inspectie" ? null : t };
}
/** Post → soort in de tabel. */
export function itemPlanType(i: { workType: WorkType; kind: ObligationKind | null }): PlanType {
  if (i.kind) return i.kind;
  return i.workType === "replacement"
    ? "vervanging"
    : i.workType === "repair"
      ? "herstel"
      : i.workType === "inspection"
        ? "inspectie"
        : "onderhoud";
}

export interface PlanRowDto {
  /** `obligation` = uitvoering bij een asset; `item` = MJOP-post (vervanging, herstel, losse regel). */
  source: "obligation" | "item";
  id: string;
  type: PlanType;
  description: string;
  basis: ObligationBasis | null;
  /** Eerstvolgende uitvoering: datum (uitvoering) of 1 januari van het startjaar (post). */
  start: string | null;
  endYear: number | null;
  /** null = eenmalig (of wekelijks). Posten altijd in hele jaren (× 12). */
  intervalMonths: number | null;
  /** Alleen bij uitvoeringen: wekelijks werk. */
  intervalWeeks: number | null;
  /** Bedrag per keer op prijspeil, excl. btw; null = onbekend of geen recht op bedragen. */
  costCents: number | null;
  /** Alleen bij uitvoeringen: materialen per keer (module Materialen); tellen op bij per keer. */
  materialsCents: number | null;
  /** Alleen bij posten: per keer = hoeveelheid × prijs per eenheid. */
  quantity: number | null;
  unit: string | null;
  /** Gemiddeld per jaar (per keer × 12 / interval). */
  perYearCents: number | null;
  /** Alleen bij uitvoeringen. */
  status: ObligationStatus | null;
  lastPerformedOn: string | null;
  lastResult: RecordResult | null;
  notApplicable: boolean;
  /** Bij herstel: bevinding. */
  findingCode: string | null;
  elementCode: string | null;
  /** Uitvoermomenten binnen de planhorizon (bedragen op prijspeil, zonder index). */
  occurrences: PlanOccurrence[];
}
export interface PlanOccurrence {
  /** Datum bij uitvoeringen; null bij posten (die hebben alleen een jaar). */
  date: string | null;
  year: number;
  amountCents: number;
  /** Post: deze keer overgeslagen. */
  skipped?: boolean;
  /** Post: oorspronkelijk jaar (sleutel om te verschuiven of te herstellen). */
  orig?: number;
  /** Post: verschoven of ander bedrag. */
  moved?: boolean;
  custom?: boolean;
  /** Post: reden bij verschuiven, overslaan of ander bedrag. */
  reason?: string;
}
export interface PlanAssetDto {
  id: string;
  code: string;
  name: string;
  quantity: number | null;
  unit: string | null;
  yearBuilt: number | null;
  elementCode: string | null;
  /** Hoofdasset als dit een onderdeel is: het staat dan in de groep van de hoofdasset, direct eronder. */
  parentAssetId: string | null;
  rows: PlanRowDto[];
}
export interface PlanGroupDto {
  /** NL-SfB op 3 posities (bv. "31.1"); null = niet ingedeeld. */
  key: string | null;
  label: string;
  assets: PlanAssetDto[];
  /** Losse regels (posten zonder asset) in deze groep. */
  loose: PlanRowDto[];
  /** Gemiddeld per jaar, alle regels in de groep. */
  perYearCents: number;
}
export interface MjopPlanDto {
  buildingId: string;
  /** Planhorizon (instellingen MJOP) en de datum van vandaag (server). */
  fromYear: number;
  toYear: number;
  today: string;
  groups: PlanGroupDto[];
  perYearCents: number;
}

/** Gemiddeld per jaar; eenmalig telt niet mee. Wekelijks rekent met 52 weken. */
export const perYear = (
  costCents: number | null,
  intervalMonths: number | null,
  intervalWeeks: number | null = null,
) =>
  costCents == null
    ? null
    : intervalWeeks
      ? Math.round((costCents * 52) / intervalWeeks)
      : intervalMonths
        ? Math.round((costCents * 12) / intervalMonths)
        : null;

/** Uitvoermomenten van een post (vervanging, herstel, losse regel) met overrides, op prijspeil. */
export function itemPlanOccurrences(
  item: BudgetItemInput,
  overrides: readonly BudgetOverrideInput[],
  fromYear: number,
  toYear: number,
): PlanOccurrence[] {
  if (item.status === "cancelled") return [];
  const base = Math.round(item.quantity * item.unitPriceCents);
  return itemOccurrences(item, overrides, toYear)
    .filter((x) => x.year >= fromYear && x.year <= toYear)
    .filter((x) => !(item.status === "done" && item.doneYear != null && x.year <= item.doneYear))
    .map((x) => ({
      date: null,
      year: x.year,
      amountCents: x.skipped ? 0 : (x.amount ?? base),
      orig: x.orig,
      ...(x.skipped ? { skipped: true } : {}),
      ...(x.year !== x.orig ? { moved: true } : {}),
      ...(x.amount != null ? { custom: true } : {}),
      ...(x.reason ? { reason: x.reason } : {}),
    }));
}

/** Uitvoermomenten van een uitvoering bij een asset, met datum, op prijspeil. */
export function obligationPlanOccurrences(
  o: Pick<
    BudgetObligationInput,
    "intervalMonths" | "intervalWeeks" | "nextDueOn" | "endYear" | "notApplicable" | "costCents"
  >,
  toYear: number,
  today: string = todayIso(),
): PlanOccurrence[] {
  return obligationDates(o, toYear, today).map((d) => ({
    date: d,
    year: Number(d.slice(0, 4)),
    amountCents: o.costCents ?? 0,
  }));
}

/** Interval zoals de tabel het toont: "1 wk", "18 mnd", "5 jr" of "eenmalig". */
export const formatIntervalOf = (months: number | null, weeks: number | null = null) =>
  weeks ? `${weeks} wk` : formatInterval(months);

/** Invoer met weken: "1 wk", "2 weken" → weken; anders zoals `parseInterval`. undefined = ongeldig. */
export function parseIntervalInput(input: string): { months: number | null; weeks: number | null } | undefined {
  const w = /^(\d+)\s*(w|wk|wkn|week|weken)$/.exec(input.trim().toLowerCase());
  if (w) {
    const n = Number(w[1]);
    return n >= 1 && n <= 52 ? { months: null, weeks: n } : undefined;
  }
  const m = parseInterval(input);
  return m === undefined ? undefined : { months: m, weeks: null };
}

/** "elke 6 mnd" / "elke 5 jr" ↔ maanden. */
export function formatInterval(months: number | null): string {
  if (!months) return "eenmalig";
  return months % 12 === 0 ? `${months / 12} jr` : `${months} mnd`;
}
/** Invoer zoals "6 mnd", "5 jr", "5", "1j", "eenmalig" → maanden; undefined = ongeldig. */
export function parseInterval(input: string): number | null | undefined {
  const s = input.trim().toLowerCase().replace(",", ".");
  if (!s || s === "eenmalig" || s === "-") return null;
  const m = /^(\d+(?:\.\d+)?)\s*(m|mnd|maand|maanden|j|jr|jaar|jaren)?$/.exec(s);
  if (!m) return undefined;
  const n = Number(m[1]);
  const months = m[2] && m[2].startsWith("m") ? n : n * 12;
  return Number.isInteger(months) && months >= 1 && months <= 600 ? months : undefined;
}
